// web/src/components/ceremony/ShardSubmitPanel.tsx — the shard-holder consent +
// submit UX (M3.4). Role-gated: only an eligible holder who has NOT yet submitted
// sees the live control; non-holders/observers see read-only progress (handled by
// the container). Shows the full consequence + requester identity BEFORE submit
// (content guide; wireframe 06):
//
//   "Submit your shard to unseal <secret> for <requester>"
//
// On confirm the holder's browser decrypts ITS OWN ciphertext shard locally
// (crypto.decryptShare — PRF-unwrap is the hardware path, software key from
// IndexedDB otherwise) and delivers the decrypted share end-to-end to the
// requester via the parent's onSubmit. The decrypted share NEVER touches the
// server; only a ciphertext-free `shard.submitted` tally is recorded (quorum.ts).
// A decline path is offered. Token-only.
import React, { useState } from 'react';
import { KeyRound, ShieldAlert } from 'lucide-react';
import { Icon } from '../ui/Icon';
import { FeedbackMessage } from '../ui';

export interface ShardSubmitPanelProps {
  /** Human label of the record being unsealed. */
  recordName: string;
  /** The requester's display identity (name/email). */
  requesterLabel: string;
  /** The requester's out-of-band safety number, if resolved. */
  requesterFingerprint?: string;
  /** K — for the consequence copy ("once K of N shards arrive"). */
  thresholdK: number;
  /** N — eligible set size. */
  totalN: number;
  /**
   * Decrypt-and-deliver this holder's own shard. The parent owns the crypto +
   * e2e transport; this panel owns only the consent gate. Rejects bubble up as a
   * calm inline error (e.g. tampered shard, key-gen failure).
   */
  onSubmit: () => Promise<void>;
  /** Decline path — audited; the requester sees the holder is outstanding. */
  onDecline: () => void;
}

export function ShardSubmitPanel({
  recordName,
  requesterLabel,
  requesterFingerprint,
  thresholdK,
  totalN,
  onSubmit,
  onDecline,
}: ShardSubmitPanelProps): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await onSubmit();
    } catch (e) {
      // Calm, non-alarmist tone (content guide / motion-spec §3.4 — no shake).
      setError(
        (e as Error)?.message ??
          'Could not decrypt or deliver your shard. You can try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bgSubmitPanel">
      <div className="bgSubmitConsequence" role="note">
        <span className="bgConsequenceIcon" aria-hidden="true">
          <Icon icon={ShieldAlert} size="md" />
        </span>
        <div>
          <p style={{ margin: 0, color: 'var(--color-text)' }}>
            <strong>{requesterLabel}</strong> is requesting to unseal{' '}
            <strong>{recordName}</strong>.
          </p>
          <p style={{ margin: '0.4rem 0 0', color: 'var(--color-text2)' }}>
            Submitting decrypts your shard on <strong>this device</strong> and
            sends it end-to-end to the requester. They will be able to reveal the
            secret once <strong>{thresholdK} of {totalN}</strong> shards arrive.
            This is logged.
          </p>
          {requesterFingerprint && (
            <p
              style={{
                margin: '0.4rem 0 0',
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--size-field-label)',
                color: 'var(--color-text2)',
              }}
            >
              Requester fingerprint: {requesterFingerprint}
            </p>
          )}
        </div>
      </div>

      {error && <FeedbackMessage ok={false} msg={error} />}

      <div className="bgSubmitActions">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onDecline}
          disabled={busy}
        >
          Decline
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={submit}
          disabled={busy}
        >
          {busy ? (
            <span className="spinner" aria-hidden="true" />
          ) : (
            <>
              <Icon icon={KeyRound} size="sm" aria-hidden="true" /> Decrypt &amp;
              submit
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default ShardSubmitPanel;
